import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { getPolicies, postPolicies } from "../../services/UserServices/UserService";
import {
  filterHtmlFields,
  languageKeys,
  languageMap,
  normalizePolicyHtml,
  preparePolicyForSave,
} from "../utils/policyUtils";
import PageTitle from "../layouts/PageTitle";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline"],
    ["link"],
  ],
};

const quillFormats = ["header", "bold", "italic", "underline", "link"];

const isHtmlEmpty = (htmlStr) => {
  if (!htmlStr) return true;
  const stripped = htmlStr.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
  return stripped.length === 0;
};

const PolicyContentPage = ({ title, policyType }) => {
  const [content, setContent] = useState({});
  const [editorContent, setEditorContent] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [activeLang, setActiveLang] = useState("html");
  const [loading, setLoading] = useState(true);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await getPolicies(policyType);
      const data = Array.isArray(response?.data?.data)
        ? response?.data?.data?.[0]
        : response?.data?.data;
      if (data) {
        const filtered = filterHtmlFields(data);
        setContent(filtered);
        setEditorContent(filtered);
      } else {
        setContent({});
        setEditorContent({});
      }
    } catch (err) {
      console.error(err);
      toast.error(`Failed to fetch ${title}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
    setActiveLang("html");
    setEditMode(false);
  }, [policyType]);

  const handleEditToggle = () => {
    if (!editMode) {
      setEditorContent({ ...content });
    }
    setEditMode(!editMode);
  };

  const handleSave = async () => {
    try {
      const payload = preparePolicyForSave(editorContent);
      await postPolicies({
        type: policyType,
        ...payload,
      });
      setContent(payload);
      setEditMode(false);
      toast.success(`${title} updated successfully!`);
    } catch (err) {
      console.error(err);
      toast.error(`Failed to update ${title}`);
    }
  };

  const currentLangHtml = normalizePolicyHtml(
    (editMode ? editorContent : content)[activeLang] || ""
  );
  const hasCurrentLangContent = !isHtmlEmpty(currentLangHtml);

  return (
    <div className="qx-page qx-policy-page">
      <PageTitle
        pageTitle={title}
        subtitle={`View and edit ${title.toLowerCase()} content`}
        actions={
          !editMode ? (
            <Button
              type="button"
              className="btn-primary qx-btn-brand"
              onClick={handleEditToggle}
            >
              Edit Content
            </Button>
          ) : null
        }
      />

      {loading ? (
        <div className="qx-policy-card">
          <p className="qx-policy-loading">Loading content...</p>
        </div>
      ) : !editMode ? (
        <div className="qx-policy-card">
          <div className="qx-seg-tabs qx-policy-lang-tabs" role="tablist">
            {languageKeys.map((lang) => (
              <button
                key={lang}
                type="button"
                role="tab"
                aria-selected={activeLang === lang}
                className={`qx-seg-tab ${activeLang === lang ? "qx-seg-tab--active" : ""}`}
                onClick={() => setActiveLang(lang)}
              >
                {languageMap[lang]}
              </button>
            ))}
          </div>

          {hasCurrentLangContent ? (
            <div
              className="qx-policy-content ql-editor"
              dangerouslySetInnerHTML={{ __html: currentLangHtml }}
            />
          ) : (
            <p className="qx-empty-state mb-0">
              No content available for {languageMap[activeLang] || "this language"}.
            </p>
          )}
        </div>
      ) : (
        <div className="qx-policy-card">
          <div className="qx-seg-tabs qx-policy-lang-tabs mb-4" role="tablist">
            {languageKeys.map((lang) => (
              <button
                key={lang}
                type="button"
                role="tab"
                aria-selected={activeLang === lang}
                className={`qx-seg-tab ${activeLang === lang ? "qx-seg-tab--active" : ""}`}
                onClick={() => setActiveLang(lang)}
              >
                {languageMap[lang]}
              </button>
            ))}
          </div>

          <div className="qx-policy-editor">
            <ReactQuill
              key={activeLang}
              theme="snow"
              value={editorContent[activeLang] || ""}
              onChange={(value, delta, source) => {
                if (source === "user") {
                  setEditorContent((prev) => ({
                    ...prev,
                    [activeLang]: value,
                  }));
                }
              }}
              modules={quillModules}
              formats={quillFormats}
            />
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button
              type="button"
              variant="outline-secondary"
              className="btn-cancel"
              onClick={handleEditToggle}
            >
              Cancel
            </Button>
            <Button type="button" className="btn-submit" onClick={handleSave}>
              Save Changes
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PolicyContentPage;
